import { Divider, Grid } from '@mui/material';
import { Formik } from 'formik';
import { memo, useMemo, useState } from 'react';
import { RJSFSchema } from '@rjsf/utils';
import type { JobDraft } from '@/redux/slices/slice_types';
import { selectValidation, selectVersionKeys } from '@/redux/selectors/orderPlacementSelectors';
import { selectWorkflows } from '@/redux/selectors/profileSelectors';
import { setValidationErrors } from '@/redux/slices/orderPlacementSlice';
import SchemaForm from '@/components/_common/SchemaForm/SchemaForm';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import type { ProfileDetailsFormHandleChange } from './OPJobProfileDetailsForm';
import OPJobProfileDetailsForm from './OPJobProfileDetailsForm';
import { applyTemplateToOrderProfile, applyTemplateToOrderSpec } from '@/_utils/orderUtils';

interface Props {
  job: JobDraft;
  onUpdateJob: (job: JobDraft) => void;
  setHaveJobsBeenEdited: (val: boolean) => void;
  schemaRef: any;
}

const doNothing = () => {};

const OPJobProfileDetails = ({ job, onUpdateJob, setHaveJobsBeenEdited, schemaRef }: Props) => {
  const dispatch = useAppDispatch();
  const workflows = useAppSelector(selectWorkflows);
  const validationErrors = useAppSelector(selectValidation);
  const workflowName = job.profile?.data?.workflow;
  const workflow = workflowName ? workflows[workflowName] : undefined;
  const [formValues, setFormValues] = useState(job.instance_inputs);
  const versionKeys = useAppSelector(selectVersionKeys);
  const orderProfileSchema: RJSFSchema | undefined = workflowName ? workflows[workflowName]?.order_profile_schema : undefined;
  const orderSpecSchema: RJSFSchema | undefined = workflowName ? workflows[workflowName]?.order_spec_schema : undefined;

  const handleChange: ProfileDetailsFormHandleChange = ({ key: field, value }) => {
    if (validationErrors[job.id]?.[field]) {
      dispatch(setValidationErrors({ jobId: job.id, field: null }));
    }

    const templateInputs = field === 'profile' ? value?.data?.template_inputs : job.profile?.data?.template_inputs;
    const hasOrderSpecSchema = Boolean(
      field === 'profile'
        ? value?.data.workflow
          ? workflows[value.data.workflow]?.order_spec_schema
          : false
        : orderSpecSchema && Object.keys(orderSpecSchema?.properties || {}).length
    );
    const hasOrderProfileSchema = Boolean(
      field === 'profile'
        ? value?.data.workflow
          ? workflows[value.data.workflow]?.order_profile_schema
          : false
        : orderProfileSchema && Object.keys(orderProfileSchema?.properties || {}).length
    );

    const { updatedOrderSpecInputs } = hasOrderSpecSchema
      ? applyTemplateToOrderSpec({
          templateInputs,
          currentOrderSpecInputs: job?.order_spec_inputs,
          orderSpecSchema: field === 'profile' && value.data.workflow ? workflows[value.data.workflow]?.order_spec_schema : orderSpecSchema
        })
      : { updatedOrderSpecInputs: undefined };

    const { updatedOrderProfileInputs } = hasOrderProfileSchema
      ? applyTemplateToOrderProfile({
          templateInputs,
          currentOrderProfileInputs: job.order_profile_inputs,
          orderProfileSchema: field === 'profile' && value.data.workflow ? workflows[value.data.workflow]?.order_profile_schema : orderProfileSchema,
          shouldReset: field === 'profile' || field === 'customer'
        })
      : { updatedOrderProfileInputs: undefined };

    onUpdateJob({
      ...job,
      [field]: value,
      ...(field === 'customer' && { profile: null, order_spec_inputs: undefined, order_profile_inputs: undefined }),
      ...(field === 'profile' && {
        order_spec_inputs: updatedOrderSpecInputs ? updatedOrderSpecInputs : undefined,
        order_profile_inputs: updatedOrderProfileInputs ? updatedOrderProfileInputs : undefined
      })
    });

    setHaveJobsBeenEdited(true);
  };

  const handleOrderSpecInputsSave = (inputs: Record<string, any> | undefined) => {
    onUpdateJob({ ...job, order_spec_inputs: inputs });
  };

  const handleSchemaErrors = (errors: any) => {
    errors.map((err: any) => {
      dispatch(setValidationErrors({ jobId: job.id, field: err.property }));
    });
  };

  const programId = useMemo(() => {
    if (job.instance_inputs?.output_version_id && versionKeys[job.instance_inputs?.output_version_id]?.program_id) {
      return versionKeys[job.instance_inputs?.output_version_id]?.program_id;
    } else {
      return job.version?.root_parent;
    }
  }, [job]);

  return (
    <Formik
      initialValues={job}
      validateOnBlur
      validateOnChange={false}
      onSubmit={doNothing /* This function is here just to stop TS compiler complaining */}
    >
      <OPJobProfileDetailsForm
        job={job}
        orderSpecForm={{
          schema: orderSpecSchema,
          inputs: job.order_spec_inputs
        }}
        orderProfileForm={{
          register: true,
          jobId: job.id,
          schema: orderProfileSchema,
          inputs: job.order_profile_inputs
        }}
        validationErrors={validationErrors[job.id]}
        additionalFields={
          workflow?.instance_schema?.properties &&
          !!Object.keys(workflow.instance_schema.properties)?.length && (
            <Grid item xs={11}>
              <Divider sx={{ mb: 3, mt: -1 }} />
              <SchemaForm
                key={`form-${job.id}-${job.profile?.id}`}
                tagName="div"
                formRef={schemaRef}
                schema={workflow?.instance_schema}
                formData={formValues}
                formContext={{ programId }}
                onChange={(data) => {
                  onUpdateJob({ ...job, instance_inputs: data.formData });
                  setFormValues((current: any) => ({
                    ...current,
                    ...(data.formData ?? {})
                  }));
                }}
                onError={handleSchemaErrors}
              />
            </Grid>
          )
        }
        handleChange={handleChange}
        updateOrderSpecInputs={handleOrderSpecInputsSave}
      />
    </Formik>
  );
};

export default memo(OPJobProfileDetails);
